/**
 * Persons VM — John Person's Volume indicator. It is a MACD computed on OBV
 * (On-Balance Volume), exactly reproduced from a thinkorswim export of the study
 * (columns MACD / MACDAvg / MACDDiff matched to 0.000 over the converged range):
 *
 *   OBV[i]   = OBV[i-1] + (Close>PrevClose ? +Vol : Close<PrevClose ? -Vol : 0)
 *   MACD     = EMA(OBV, 5) − EMA(OBV, 14)     ← green line   (the "MACD" plot)
 *   signal   = EMA(MACD, 9)                   ← magenta line (the "MACDAvg" plot)
 *   histogram = MACD − signal                 ← green when ≥ 0 / red when < 0
 *                                               (the "MACDDiff" plot)
 *
 * OBV is what gives the direction: up-closes accumulate volume, down-closes shed
 * it, so on a high-volume WEAK close (close < prior close) OBV drops sharply, the
 * MACD line falls below its signal, and the histogram turns RED — matching the
 * real chart. The EMAs seed at the FIRST DEFINED OBV value — the 2nd bar, where
 * the close-direction first exists (the OBV[0] base of 0 is excluded) — so MACD
 * is 0 on bars 1–2 and the signal seeds at that 0. Verified vs a thinkorswim
 * export (MACD / MACDAvg / MACDDiff) to 0.00 across every bar; seeding at the
 * OBV[0]=0 base instead is wrong (off by millions in the early/medium range).
 *
 * Lengths stay configurable. The defaults are 5 / 14 / 9, SweeterTrade's Person's
 * Volume Histogram (`pvh`, `pvhSignal`, `pvhHist`), which the owner chose
 * (`reports/SweeterTrade indicators and universes audit.md`: 100% of META's and
 * NVDA's daily bars). Until 2026-11 they were thinkorswim's 6 / 18 / 9, which
 * that export was matched with and which matched none of theirs.
 */
import { familyGroup } from "../indicatorGroups";
import { TF_DWM, TF_PARAM } from "./timeframe";
import { resampleOHLC, resampleVolumes, htIndexPerDay, type HtFreq } from "./weekly";
import type { OHLCV, IndicatorParams, SharedIndicatorDef, SharedParam } from "../types";

export type VmParts = {
  fast: (number | null)[]; // MACD = EMA(OBV,fast) − EMA(OBV,slow) — green line
  slow: (number | null)[]; // signal = EMA(MACD, signalLen)        — magenta line
  hist: (number | null)[]; // MACD − signal (the histogram)
  state: (number | null)[]; // +1 green (hist ≥ 0) / -1 red (hist < 0)
};

/** The default lengths, the chart study's and the scanner's alike. */
export const VM_FAST_LEN = 5;
export const VM_SLOW_LEN = 14;
export const VM_SIGNAL_LEN = 9;

/**
 * A chart saved with the retired defaults 6 / 18 / 9 (all three) reads today's,
 * so a saved chart and the scanner's columns beside it are one indicator (the
 * owner, 2026-11-02: "5/14/9 everywhere, saved charts too"). Any other saved
 * lengths are used as set. Its Inputs box may still show the numbers it was
 * saved with.
 */
export function vmLengthsOf(params: Record<string, unknown>): { fastLen: number; slowLen: number; signalLen: number } {
  const fastLen = len(params.fastLen, VM_FAST_LEN);
  const slowLen = len(params.slowLen, VM_SLOW_LEN);
  const signalLen = len(params.signalLen, VM_SIGNAL_LEN);
  if (fastLen === 6 && slowLen === 18 && signalLen === 9) {
    return { fastLen: VM_FAST_LEN, slowLen: VM_SLOW_LEN, signalLen: VM_SIGNAL_LEN };
  }
  return { fastLen, slowLen, signalLen };
}

const num = (v: unknown, d: number): number =>
  typeof v === "number" && Number.isFinite(v) ? v : d;
const len = (v: unknown, d: number): number => Math.max(1, Math.round(num(v, d)));

/** EMA seeded at index 1 — the first DEFINED OBV value (the 2nd bar, where the
 *  close-direction first exists), matching thinkorswim. NaN at index 0. */
function emaSeed1(x: number[], L: number): number[] {
  const n = x.length;
  const a = 2 / (L + 1);
  const out = new Array<number>(n).fill(NaN);
  let prev = NaN;
  for (let i = 1; i < n; i++) {
    prev = i === 1 ? x[1]! : a * (x[i]! - prev) + prev;
    out[i] = prev;
  }
  return out;
}

export function computeVM(data: OHLCV, params: IndicatorParams = {}): VmParts {
  const { closes, volumes } = data;
  const n = closes.length;
  const empty = (): (number | null)[] => new Array(n).fill(null);
  if (!volumes || n === 0) return { fast: empty(), slow: empty(), hist: empty(), state: empty() };

  const fastLen = len(params.fastLen, VM_FAST_LEN);
  const slowLen = len(params.slowLen, VM_SLOW_LEN);
  const signalLen = len(params.signalLen, VM_SIGNAL_LEN);

  // On-Balance Volume — cumulative signed volume by close direction.
  const obv = new Array<number>(n).fill(0);
  for (let i = 1; i < n; i++) {
    const v = Number.isFinite(volumes[i]!) ? volumes[i]! : 0;
    const dc = closes[i]! - closes[i - 1]!;
    obv[i] = obv[i - 1] + (dc > 0 ? v : dc < 0 ? -v : 0);
  }

  // MACD of OBV: line = fast EMA − slow EMA; signal = EMA of the line. The OBV
  // EMAs seed at index 1 (first defined OBV), so MACD[1] = 0 and the signal
  // seeds at that 0 — index 0 stays undefined (warmup).
  const ef = emaSeed1(obv, fastLen);
  const es = emaSeed1(obv, slowLen);
  const macd = new Array<number>(n).fill(NaN);
  for (let i = 1; i < n; i++) macd[i] = ef[i]! - es[i]!;
  const sigAlpha = 2 / (signalLen + 1);
  const sig = new Array<number>(n).fill(NaN);
  let sp = NaN;
  for (let i = 1; i < n; i++) {
    sp = i === 1 ? macd[1]! : sigAlpha * (macd[i]! - sp) + sp;
    sig[i] = sp;
  }

  const fast: (number | null)[] = new Array(n).fill(null);
  const slow: (number | null)[] = new Array(n).fill(null);
  const hist: (number | null)[] = new Array(n).fill(null);
  const state: (number | null)[] = new Array(n).fill(null);
  for (let i = 0; i < n; i++) {
    if (!Number.isFinite(macd[i]!) || !Number.isFinite(sig[i]!)) continue; // index 0: warmup
    const h = macd[i]! - sig[i]!;
    fast[i] = macd[i]!;
    slow[i] = sig[i]!;
    hist[i] = h;
    state[i] = h >= 0 ? 1 : -1;
  }
  return { fast, slow, hist, state };
}

export const VM_PARAMS: ReadonlyArray<SharedParam> = [
  { id: "fastLen", label: "Fast EMA", kind: "number", default: VM_FAST_LEN, min: 1, max: 100, step: 1 },
  { id: "slowLen", label: "Slow EMA", kind: "number", default: VM_SLOW_LEN, min: 2, max: 200, step: 1 },
  { id: "signalLen", label: "Signal EMA", kind: "number", default: VM_SIGNAL_LEN, min: 1, max: 100, step: 1 },
];

const tfFreq = (tf: number | undefined): HtFreq | "day" =>
  tf === 2 ? "month" : tf === 1 ? "week" : "day";

/** VM histogram (MACD − signal of OBV) on the chosen timeframe, projected onto
 *  daily bars. Daily runs on daily closes/volumes; Weekly/Monthly resample
 *  (in-progress bucket included) and project by period. */
function vmTfSeries(data: OHLCV, p: IndicatorParams, freq: HtFreq | "day"): (number | null)[] {
  if (freq === "day") return computeVM(data, p).hist;
  const { times, opens, highs, lows, closes, volumes } = data;
  const n = closes.length;
  if (!times || times.length !== n || !volumes || !opens || !highs || !lows) return new Array(n).fill(null);
  const h = resampleOHLC(times, opens, highs, lows, closes, freq);
  const v = resampleVolumes(times, volumes, freq);
  const hist = computeVM({ closes: h.c, volumes: v } as OHLCV, p).hist;
  const idx = htIndexPerDay(times, h.t);
  return times.map((_, i) => { const k = idx[i]!; return k >= 0 && k < hist.length ? hist[k]! : null; });
}

/** Scanner / watchlist scalar — the latest VM histogram (MACD − signal of OBV) on
 *  a Daily/Weekly/Monthly Timeframe select. Green ≥ 0 (accumulation) / red < 0
 *  (distribution). Reads the baked vol_hist_d/w/m columns at default params. */
export const PERSONS_VM: SharedIndicatorDef = {
  id: "jp-vm",
  label: "Afiat - OBV MACD",
  group: familyGroup("vm"), // → "Pivot Studies"
  format: "signed",
  panelKey: "vol_hist_d",
  params: [TF_DWM, ...VM_PARAMS],
  computeSeries: (data, params) => vmTfSeries(data, params, tfFreq(params[TF_PARAM])),
};

export const PERSONS_VM_INDICATORS: ReadonlyArray<SharedIndicatorDef> = [PERSONS_VM];
